import { useState } from 'react';
import { When } from 'react-if';
import { PhoneIcon } from '@heroicons/react/20/solid';
import { MixpanelEvents, getAccountingSystemDeepLink } from 'shared';

import { IInlineSelectOption, InlineSelect } from '../../../../../common/Atoms/InlineSelect';
import { PillBadge } from '../../../../../common/Atoms/PillBadge';
import { ToolbarButton } from '../../../../../common/Atoms/ToolbarButton';
import { ITab } from '../../../../../common/Atoms/Tabs';
import { UnderlineTabs } from '../../../../../common/Atoms/UnderlineTabs';
import { env } from '../../../../../common/lib/env';
import { mixpanelService } from '../../../../../common/lib/mixpanel';
import { useAccountingSystemTerm } from '../../../../hooks/useAccountingSystemTerm';
import { useAdhocActionModal } from '../../../../hooks/useAdhocActionModal';
import { useContact } from '../../../../hooks/useContact';
import { useContactQuotes } from '../../../../hooks/useContactQuotes';
import { useCurrentOrgUsers } from '../../../../hooks/useCurrentOrgUsers';
import { useGetSelectedOrganisation } from '../../../../hooks/useGetSelectedOrganisation';
import { useGroups } from '../../../../hooks/useGroups';
import {
  useAssignContactMutation,
  useClearAssignedContactMutation,
  useToggleContactFreezeMutation,
} from '../../../../services/api/contactsApi/contact';
import { useApplyTagMutation, useClearTagsMutation } from '../../../../services/api/contactsApi/contacts';
import { useAddContactsToGroupMutation } from '../../../../services/api/groupApi/group';
import { RaiseEscalationModal } from '../../../Escalations/RaiseEscalationModal';
import { FrozenToggle } from '../../../Molecules/FrozenToggle';
import { WrappedLink } from '../../../WrappedLink';

import { CallToolbarButton, useClickToCall } from './ClickToCall';
import { ContactChildLinks, ContactParentLink } from './ContactParentChildLinks';
import { ContactCreditScoreBadge } from './ContactCreditScoreBadge';
import { TagPills } from './TagPills';
import { useContactAllEscalations, useContactOpenQuotes } from './contactPageData';

const linkClass = `text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline`;
const controlClass = `inline-flex h-[1.625rem] items-center gap-1.5 rounded-md border border-gray-200 px-2 text-[0.8125rem]`;
const countClass = `bg-transparent px-0 text-sm font-normal text-gray-400`;

function count(n: number) {
  return n > 0 ? { text: String(n), className: countClass } : undefined;
}

export function ContactHeaderV2() {
  const { data: contact } = useContact();
  const organisation = useGetSelectedOrganisation();
  const quotesTerm = useAccountingSystemTerm(`Quotes`);
  const { quotes } = useContactQuotes();
  const openQuotes = useContactOpenQuotes();
  const { open: openEscalations } = useContactAllEscalations();
  const { data: groups } = useGroups();
  const orgUsers = useCurrentOrgUsers(true);
  const { openChargeInterest, openSendStatement } = useAdhocActionModal();
  const [toggleFreeze, { isLoading: freezeLoading }] = useToggleContactFreezeMutation();
  const [addToGroup, { isLoading: groupLoading }] = useAddContactsToGroupMutation();
  const [assignContact, { isLoading: assignLoading }] = useAssignContactMutation();
  const [clearAssignedContact, { isLoading: clearAssignLoading }] = useClearAssignedContactMutation();
  const [applyTag, { isLoading: applyTagLoading }] = useApplyTagMutation();
  const [removeTag, { isLoading: removeTagLoading }] = useClearTagsMutation();
  const [raiseEscalationOpen, setRaiseEscalationOpen] = useState(false);
  const { enabled: canCall, browserEnabled, call, busy: callBusy, targets: callTargets } = useClickToCall();

  if (!contact || !organisation) return null;

  const contactExternalLink = getAccountingSystemDeepLink(organisation, `contact`, contact.externalId);

  const hasQuotes = Object.values(quotes).some(statusQuotes => statusQuotes?.length);

  const tabs: ITab[] = [
    { name: `Activity`, href: `` },
    { name: `Invoices`, href: `invoices`, secondaryBadge: count(contact.unpaidInvoiceCount || 0) },
    ...(hasQuotes ? [{ name: quotesTerm, href: `quotes`, secondaryBadge: count(openQuotes.length) }] : []),
    { name: `Comms`, href: `comms` },
    { name: `Actions`, href: `activity` },
    // Different name for navigation highlight
    { name: `Escalations`, href: `tasks`, secondaryBadge: count(openEscalations.length) },
    { name: `Statement`, href: `statement` },
    { name: `Applicable Policies`, href: `policies` },
  ];

  const ownerOptions: IInlineSelectOption<string | null>[] = [
    { label: `Unassigned`, value: null },
    ...orgUsers,
  ];

  if (contact.assignedUserId && !orgUsers.some(u => u.value === contact.assignedUserId)) {
    ownerOptions.push({ label: `Removed user`, value: contact.assignedUserId, disabled: true });
  }

  function onAssign(userId: string | null) {
    if (!userId) {
      if (contact.assignedUserId) {
        clearAssignedContact({ organisationId: contact.organisationId, contactId: contact.id });
      }

      return;
    }

    assignContact({ organisationId: contact.organisationId, contactId: contact.id, userId });
  }

  function onTagsChange(next: string[]) {
    const removed = contact.tags.find(tag => !next.includes(tag));

    if (removed) {
      removeTag({ organisationId: organisation.id, contactIds: [contact.id], tagName: removed });

      return;
    }

    const added = next.find(tag => !contact.tags.includes(tag));

    if (added) {
      applyTag({ organisationId: organisation.id, contactIds: [contact.id], tag: added });
    }
  }

  function onSendStatement() {
    openSendStatement(contact.id, contact.name);
    mixpanelService.trackMixpanelEvent(MixpanelEvents.ContactPageSendStatementNowClick);
  }

  function onChargeInterest() {
    openChargeInterest(contact.id, contact.name);
    mixpanelService.trackMixpanelEvent(MixpanelEvents.DashboardChargeInterestNowClick);
  }

  return (
    // Full width bar under the top nav. Cancels the page container's side padding (px-4 sm:px-6 md:px-8)
    // and puts it back inside, so the title, controls and tabs line up with the cards below
    <div className={ `-mx-4 flex shrink-0 flex-col gap-3.5 bg-white px-4 pt-4 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8` }>
      <div className={ `flex flex-wrap items-start justify-between gap-x-6 gap-y-3` }>
        <div className={ `flex min-w-0 flex-col gap-1` }>
          <ContactParentLink />
          <div className={ `flex flex-wrap items-center gap-2.5` }>
            <h1 className={ `text-xl font-semibold text-gray-900` }>{ contact.name }</h1>
            <ContactCreditScoreBadge />
            <When condition={ !!contact.externalAccountNumber }>
              <span className={ `text-[0.8125rem] text-gray-500` }>{ contact.externalAccountNumber }</span>
            </When>
            <When condition={ openEscalations.length > 0 }>
              <WrappedLink
                to={ `/contacts/${contact.id}/tasks` }
                className={ `hover:underline` }
              >
                <PillBadge
                  color={ `red` }
                  icon={ PhoneIcon }
                >
                  { `Escalated` }
                </PillBadge>
              </WrappedLink>
            </When>
          </div>
          <ContactChildLinks />

          <div className={ `mt-0.5 flex flex-wrap items-center gap-1.5 text-[0.8125rem] text-gray-700` }>
            <When condition={ !!contact.group }>
              <span className={ controlClass }>
                <span className={ `text-gray-500` }>{ `Group` }</span>
                <InlineSelect
                  options={ (groups || []).map(group => ({ value: group.id, label: group.title })) }
                  value={ contact.group?.id }
                  onChange={ id => addToGroup({ organisationId: contact.organisationId, id, contactIds: [contact.id] }) }
                  display={ contact.group?.title }
                  disabled={ groupLoading }
                />
              </span>
            </When>
            <span className={ controlClass }>
              <span className={ `text-gray-500` }>{ `Owner` }</span>
              <InlineSelect
                options={ ownerOptions }
                value={ contact.assignedUserId || null }
                onChange={ onAssign }
                disabled={ assignLoading || clearAssignLoading }
              />
            </span>
            <TagPills
              tags={ contact.tags }
              onChange={ onTagsChange }
              disabled={ applyTagLoading || removeTagLoading }
            />
            <span className={ `mx-1 h-4 w-px bg-gray-200` } />
            <When condition={ !!contactExternalLink }>
              <a
                href={ contactExternalLink }
                target={ `_blank` }
                rel={ `noopener noreferrer` }
                className={ linkClass }
              >
                { `Edit in ${organisation.accountingSystemName} ↗` }
              </a>
            </When>
            <a
              href={ `${env.portalWebUrl}/c/${contact.hash}` }
              target={ `_blank` }
              rel={ `noopener noreferrer` }
              className={ linkClass }
            >
              { `Customer portal ↗` }
            </a>
          </div>
        </div>

        <div className={ `flex flex-wrap items-center gap-1.5` }>
          <When condition={ canCall }>
            <CallToolbarButton
              targets={ callTargets }
              browserEnabled={ browserEnabled }
              onCall={ call }
              disabled={ callBusy }
            />
          </When>
          <ToolbarButton onClick={ onSendStatement }>
            { `Send statement` }
          </ToolbarButton>
          <ToolbarButton onClick={ () => setRaiseEscalationOpen(true) }>
            { `Raise escalation` }
          </ToolbarButton>
          <ToolbarButton
            onClick={ onChargeInterest }
            className={ `border-orange-300 text-orange-700 hover:border-orange-300 hover:bg-orange-50` }
          >
            { `Charge interest` }
          </ToolbarButton>
          <FrozenToggle
            value={ contact.frozen || false }
            onChange={ () => toggleFreeze({ contactId: contact.id, organisationId: organisation.id }) }
            name={ `frozen` }
            activeText={ `Contact frozen` }
            inactiveText={ `Freeze contact` }
            disabled={ freezeLoading }
            className={ `ml-1.5` }
            tooltip={ `Freezing prevents any policies applying to this contact` }
          />
        </div>
      </div>

      <UnderlineTabs
        dense
        tabs={ tabs }
        className={ `-mx-4 px-4 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8` }
      />

      <RaiseEscalationModal
        open={ raiseEscalationOpen }
        setOpen={ setRaiseEscalationOpen }
        contact={ { id: contact.id, name: contact.name } }
      />
    </div>
  );
}
